import { Point } from 'pixi.js';
import { GridSystem } from '@gameplay/building/GridSystem';
import { WallSystem } from '@gameplay/building/WallSystem';
import { DoorSystem } from '@gameplay/building/DoorSystem';

interface PathNode {
  x: number;
  y: number;
  gCost: number; // Distance from start
  hCost: number; // Distance to end (heuristic)
  fCost: number; // gCost + hCost
  parent: PathNode | null;
}

export class PathfindingSystem {
  private readonly gridSystem: GridSystem;
  private readonly wallSystem: WallSystem;
  private readonly doorSystem: DoorSystem;
  private readonly maxSearchDistance: number = 50; // Limit search area for performance
  private readonly pathCache: Map<string, { path: Point[], timestamp: number }> = new Map();
  private readonly cacheTimeout: number = 5000; // Cache paths for 5 seconds

  constructor(gridSystem: GridSystem, wallSystem: WallSystem, doorSystem: DoorSystem) {
    this.gridSystem = gridSystem;
    this.wallSystem = wallSystem;
    this.doorSystem = doorSystem;
  }

  /**
   * Find a path from start to end using A* pathfinding algorithm
   * Returns array of world coordinates for smooth movement
   */
  public findPath(start: Point, end: Point, floor: number = 0): Point[] {
    // Check cache first
    const cacheKey = this.getPathCacheKey(start, end, floor);
    const cached = this.pathCache.get(cacheKey);
    const now = Date.now();
    
    if (cached && now - cached.timestamp < this.cacheTimeout) {
      return [...cached.path]; // Return copy to prevent modification
    }
    
    const startGrid = this.worldToGrid(start);
    const endGrid = this.worldToGrid(end);
    
    // Quick check if end position is reachable
    if (!this.isWalkable(endGrid.x, endGrid.y, floor)) {
      // Try to find a nearby walkable position
      const nearbyEnd = this.findNearbyWalkable(endGrid, floor);
      if (!nearbyEnd) {
        return []; // No path possible
      }
      endGrid.x = nearbyEnd.x;
      endGrid.y = nearbyEnd.y;
    }

    const gridPath = this.findGridPath(startGrid, endGrid, floor);
    if (gridPath.length === 0) {
      return []; // No path found
    }

    // Convert grid path to world coordinates
    const worldPath = gridPath.map(gridPos => this.gridToWorld(gridPos));
    
    // Cache the result
    this.pathCache.set(cacheKey, { path: [...worldPath], timestamp: now });
    
    // Clean old cache entries periodically
    if (this.pathCache.size > 100) {
      this.cleanCache(now);
    }
    
    return worldPath;
  }

  private findGridPath(start: Point, end: Point, floor: number): Point[] {
    const openSet: PathNode[] = [];
    const closedSet: Set<string> = new Set();
    
    const startNode: PathNode = {
      x: start.x,
      y: start.y,
      gCost: 0,
      hCost: this.getDistance(start, end),
      fCost: 0,
      parent: null
    };
    startNode.fCost = startNode.gCost + startNode.hCost;
    
    openSet.push(startNode);

    while (openSet.length > 0) {
      // Find node with lowest fCost
      let currentIndex = 0;
      for (let i = 1; i < openSet.length; i++) {
        if (openSet[i].fCost < openSet[currentIndex].fCost) {
          currentIndex = i;
        }
      }
      
      const current = openSet[currentIndex];
      openSet.splice(currentIndex, 1);
      closedSet.add(this.getNodeKey(current));

      // Check if we reached the end
      if (current.x === end.x && current.y === end.y) {
        return this.reconstructPath(current);
      }

      // Check all neighbors
      const neighbors = this.getNeighbors(current, floor);
      for (const neighbor of neighbors) {
        const nodeKey = this.getNodeKey(neighbor);
        if (closedSet.has(nodeKey)) {
          continue;
        }

        const tentativeGCost = current.gCost + this.getDistance(
          new Point(current.x, current.y), 
          new Point(neighbor.x, neighbor.y)
        );

        const existingNode = openSet.find(n => n.x === neighbor.x && n.y === neighbor.y);
        if (!existingNode) {
          neighbor.gCost = tentativeGCost;
          neighbor.hCost = this.getDistance(new Point(neighbor.x, neighbor.y), end);
          neighbor.fCost = neighbor.gCost + neighbor.hCost;
          neighbor.parent = current;
          openSet.push(neighbor);
        } else if (tentativeGCost < existingNode.gCost) {
          existingNode.gCost = tentativeGCost;
          existingNode.fCost = existingNode.gCost + existingNode.hCost;
          existingNode.parent = current;
        }
      }

      // Performance limit: don't search too far
      if (Math.abs(current.x - start.x) > this.maxSearchDistance || 
          Math.abs(current.y - start.y) > this.maxSearchDistance) {
        break;
      }
    }

    return []; // No path found
  }

  private getNeighbors(node: PathNode, floor: number): PathNode[] {
    const neighbors: PathNode[] = [];
    const directions = [
      { dx: 0, dy: 1 },   // North
      { dx: 1, dy: 0 },   // East
      { dx: 0, dy: -1 },  // South
      { dx: -1, dy: 0 },  // West
      { dx: 1, dy: 1 },   // Northeast
      { dx: 1, dy: -1 },  // Southeast
      { dx: -1, dy: -1 }, // Southwest
      { dx: -1, dy: 1 }   // Northwest
    ];

    for (const dir of directions) {
      const newX = node.x + dir.dx;
      const newY = node.y + dir.dy;

      if (this.isWalkable(newX, newY, floor) && !this.isWallBlocking(node.x, node.y, newX, newY, floor)) {
        neighbors.push({
          x: newX,
          y: newY,
          gCost: 0,
          hCost: 0,
          fCost: 0,
          parent: null
        });
      }
    }

    return neighbors;
  }

  private isWalkable(x: number, y: number, floor: number): boolean {
    // Must have floor to walk on
    if (!this.gridSystem.hasFloor(x, y, floor)) {
      return false;
    }

    // Cannot walk through furniture
    if (this.gridSystem.hasFurniture(x, y, floor)) {
      return false;
    }

    return true;
  }

  private isWallBlocking(fromX: number, fromY: number, toX: number, toY: number, floor: number): boolean {
    const dx = toX - fromX;
    const dy = toY - fromY;
    
    // For orthogonal movement, check direct wall and door
    if (Math.abs(dx) + Math.abs(dy) === 1) {
      const hasWall = this.wallSystem.isOccupied(fromX, fromY, toX, toY, floor);
      if (!hasWall) return false; // No wall, no blocking
      
      // Check if there's an open door at this position
      const hasOpenDoor = this.doorSystem.isDoorBlocking(fromX, fromY, toX, toY, floor);
      return hasOpenDoor; // Door blocks only if closed
    }
    
    // For diagonal movement, check both orthogonal paths
    if (Math.abs(dx) === 1 && Math.abs(dy) === 1) {
      // Check horizontal path: from -> (toX, fromY) -> to
      const horizontalWall = this.wallSystem.isOccupied(fromX, fromY, toX, fromY, floor);
      const horizontalDoorBlocked = horizontalWall && this.doorSystem.isDoorBlocking(fromX, fromY, toX, fromY, floor);
      
      // Check vertical path: from -> (fromX, toY) -> to  
      const verticalWall = this.wallSystem.isOccupied(fromX, fromY, fromX, toY, floor);
      const verticalDoorBlocked = verticalWall && this.doorSystem.isDoorBlocking(fromX, fromY, fromX, toY, floor);
      
      // If both paths are blocked by walls/closed doors, diagonal movement is blocked
      return (horizontalWall && horizontalDoorBlocked) && (verticalWall && verticalDoorBlocked);
    }
    
    // For any other movement (should not happen in grid-based pathfinding), assume not blocked
    return false;
  }

  private findNearbyWalkable(center: Point, floor: number): Point | null {
    // Search in expanding rings for a walkable position
    for (let radius = 1; radius <= 5; radius++) {
      for (let x = center.x - radius; x <= center.x + radius; x++) {
        for (let y = center.y - radius; y <= center.y + radius; y++) {
          if (this.isWalkable(x, y, floor)) {
            return new Point(x, y);
          }
        }
      }
    }
    return null;
  }

  private reconstructPath(endNode: PathNode): Point[] {
    const path: Point[] = [];
    let current: PathNode | null = endNode;

    while (current) {
      path.unshift(new Point(current.x, current.y));
      current = current.parent;
    }

    // Remove the starting position since the resident is already there
    path.shift();
    
    return path;
  }

  private getDistance(a: Point, b: Point): number {
    // Use Manhattan distance for grid-based pathfinding
    return Math.abs(a.x - b.x) + Math.abs(a.y - b.y);
  }

  private getNodeKey(node: PathNode): string {
    return `${node.x},${node.y}`;
  }

  private gridToWorld(gridPos: Point): Point {
    return this.gridSystem.gridToWorld(gridPos.x, gridPos.y);
  }

  private worldToGrid(worldPos: Point): Point {
    return this.gridSystem.worldToGrid(worldPos.x, worldPos.y);
  }

  /**
   * Check if a direct line between two world positions is clear of obstacles
   * Useful for validation or simple movement
   */
  public isDirectPathClear(start: Point, end: Point, floor: number = 0): boolean {
    const startGrid = this.worldToGrid(start);
    const endGrid = this.worldToGrid(end);
    
    // Use Bresenham's line algorithm to check each grid position along the path
    const positions = this.getLinePositions(startGrid, endGrid);
    
    for (const pos of positions) {
      if (!this.isWalkable(pos.x, pos.y, floor)) {
        return false;
      }
    }

    return true;
  }

  private getLinePositions(start: Point, end: Point): Point[] {
    const positions: Point[] = [];
    const dx = Math.abs(end.x - start.x);
    const dy = Math.abs(end.y - start.y);
    const sx = start.x < end.x ? 1 : -1;
    const sy = start.y < end.y ? 1 : -1;
    let err = dx - dy;
    
    let x = start.x;
    let y = start.y;
    
    while (true) {
      positions.push(new Point(x, y));
      
      if (x === end.x && y === end.y) break;
      
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
    
    return positions;
  }
  
  private getPathCacheKey(start: Point, end: Point, floor: number): string {
    return `${start.x.toFixed(1)},${start.y.toFixed(1)}-${end.x.toFixed(1)},${end.y.toFixed(1)}-${floor}`;
  }
  
  private cleanCache(currentTime: number): void {
    const keysToDelete: string[] = [];
    
    for (const [key, entry] of this.pathCache.entries()) {
      if (currentTime - entry.timestamp >= this.cacheTimeout) {
        keysToDelete.push(key);
      }
    }
    
    keysToDelete.forEach(key => this.pathCache.delete(key));
  }
}